import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import moment from "moment";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: { padding: theme.spacing(3) },
  section: { padding: theme.spacing(3), marginBottom: theme.spacing(3) },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: "4px 0",
  },
  actions: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(2) },
  error: { color: "#c62828" },
  ok: { color: "#2e7d32" },
  chart: { width: "100%", height: 280, marginTop: theme.spacing(1) },
});

function hasCloudWatch(samples) {
  return (samples || []).some(
    (sample) => sample && (sample.mem != null || sample.cpu != null || sample.readOps != null)
  );
}

function series(samples, source) {
  return (samples || [])
    .filter((sample) => sample.source === source && sample.at)
    .slice()
    .sort((a, b) => new Date(a.at) - new Date(b.at))
    .map((sample) => ({
      ...sample,
      label: moment(sample.at).format(source === "probe" ? "HH:mm" : "DD/MM HH:mm"),
      pingMs: sample.pingOk === false ? null : sample.pingMs,
    }));
}

class DbHealthMonitor extends Component {
  state = {
    loading: true,
    error: null,
    notice: null,
    status: null,
    dryRun: true,
    autoRestartEnabled: false,
    memoryWarnPercent: 88,
    swapWarnPercent: 90,
    ioReadOpsWarn: 300000,
    cpuWarnPercent: 40,
    preflightRunning: false,
    preflight: null,
  };

  componentDidMount() {
    this.load();
    this.timer = setInterval(this.load, 30000);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  load = async () => {
    try {
      const res = await fetch(`${basePath}/api/v1/admin/db-guardian`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to load");
      this.setState((prev) => {
        const incomingUseful =
          hasCloudWatch(data.samples) || !hasCloudWatch(prev.status?.samples);
        const status = incomingUseful ? data : prev.status;
        const settings = status?.settings || {};
        const thresholds = settings.thresholds || {};
        return {
          loading: false,
          error: null,
          status,
          dryRun: settings.dryRun !== false,
          autoRestartEnabled: Boolean(settings.autoRestartEnabled),
          memoryWarnPercent: thresholds.memoryWarnPercent ?? 88,
          swapWarnPercent: thresholds.swapWarnPercent ?? 90,
          ioReadOpsWarn: thresholds.ioReadOpsWarn ?? 300000,
          cpuWarnPercent: thresholds.cpuWarnPercent ?? 40,
        };
      });
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  };

  save = async () => {
    const {
      dryRun,
      autoRestartEnabled,
      memoryWarnPercent,
      swapWarnPercent,
      ioReadOpsWarn,
      cpuWarnPercent,
    } = this.state;
    this.setState({ notice: null, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/db-guardian/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          dryRun,
          autoRestartEnabled,
          thresholds: {
            memoryWarnPercent: Number(memoryWarnPercent),
            swapWarnPercent: Number(swapWarnPercent),
            ioReadOpsWarn: Number(ioReadOpsWarn),
            cpuWarnPercent: Number(cpuWarnPercent),
          },
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Save failed");
      this.setState({ notice: "ההגדרות נשמרו" });
      this.load();
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  preflight = async () => {
    this.setState({ preflightRunning: true, notice: null, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/db-guardian/preflight`, {
        method: "POST",
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "הבדיקה נכשלה");
      this.setState({
        preflightRunning: false,
        preflight: data.preflight,
        notice: data.preflight?.ok
          ? "שני השרתים מוכנים"
          : "הבדיקה מצאה חוסרים",
      });
    } catch (err) {
      this.setState({ preflightRunning: false, error: err.message });
    }
  };

  post = async (path) => {
    this.setState({ notice: null, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/db-guardian/${path}`, {
        method: "POST",
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Request failed");
      this.setState({ notice: data.message || "בוצע" });
      this.load();
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  renderReport(title, report) {
    const { classes } = this.props;
    if (!report) return null;
    const who = [report.name, report.localIp].filter(Boolean).join(" ");
    return (
      <div>
        <Typography variant="subtitle1">
          {title}{who ? ` — ${who}` : ""}
        </Typography>
        {(report.checks || []).map((item) => (
          <div key={item.id} className={classes.row}>
            <Typography variant="body2">{item.label}</Typography>
            <Typography variant="body2" className={item.ok ? classes.ok : classes.error}>
              {item.ok ? "תקין" : "חסר"} — {item.detail}
            </Typography>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const { loading, error, notice, status } = this.state;
    if (loading && !status) return <CircularProgress />;
    const evaluation = status?.evaluation || {};
    const state = status?.state || {};
    const metrics = series(status?.samples, "cloudwatch");
    const pings = series(status?.samples, "probe");
    const events = status?.events || [];

    return (
      <div className={classes.root}>
        <Paper className={classes.section}>
          <Typography variant="h6">מצב נוכחי</Typography>
          <div className={classes.row}>
            <Typography variant="body2" color="textSecondary">שלב</Typography>
            <Chip label={state.phase || "—"} />
          </div>
          <div className={classes.row}>
            <Typography variant="body2" color="textSecondary">תפקיד</Typography>
            <Typography variant="body2">
              {status?.role} {status?.actingPrimary ? "(מוביל)" : ""}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography variant="body2" color="textSecondary">מצב תחזוקה פנימי</Typography>
            <Typography variant="body2">{status?.degraded ? "פעיל" : "כבוי"}</Typography>
          </div>
          <div className={classes.row}>
            <Typography variant="body2" color="textSecondary">התראה</Typography>
            <Typography variant="body2">
              {evaluation.level || "none"} {(evaluation.codes || []).join(", ")}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography variant="body2" color="textSecondary">צילום קטלוג</Typography>
            <Typography variant="body2">{status?.snapshotAt || "עדיין אין"}</Typography>
          </div>
          {error && <Typography className={classes.error}>{error}</Typography>}
          {notice && <Typography className={classes.ok}>{notice}</Typography>}
          <div className={classes.actions}>
            <Button variant="contained" color="primary" onClick={() => this.post("restart")}>
              הפעלה מחדש בחלון שקט
            </Button>
            <Button variant="outlined" onClick={() => this.post("abort")}>
              ביטול
            </Button>
          </div>
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6">בדיקת מוכנות</Typography>
          <Typography variant="body2">
            מריצים אחרי פריסה, לפני כיבוי dry-run והדלקת הפעלה אוטומטית. הבדיקה בודקת את הקונטיינר הזה ואת השני: כתובת פנימית, סוד, תיקייה, מענה על ה-IP הפנימי, הרשאת הפעלה מחדש, מדדי RAM ופינג למסד. היא לא מפעילה מחדש את המסד ולא שולחת מייל.
          </Typography>
          <div className={classes.actions}>
            <Button
              variant="outlined"
              onClick={this.preflight}
              disabled={this.state.preflightRunning}
            >
              {this.state.preflightRunning ? "בודק…" : "הרצת בדיקה"}
            </Button>
          </div>
          {this.renderReport("השרת הזה", this.state.preflight?.local)}
          {this.renderReport("השרת השני", this.state.preflight?.remote)}
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6">סף והפעלה</Typography>
          <Typography variant="body2">
            ברירת המחדל היא dry-run עם הפעלה אוטומטית כבויה. אחרי תרגול לילי אחד אפשר לכבות dry-run ולהדליק הפעלה אוטומטית. חלון הלילה הוא 02:00–05:00 שעון ישראל.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={this.state.dryRun}
                onChange={(e) => this.setState({ dryRun: e.target.checked })}
              />
            }
            label={<Typography variant="body2">Dry run (שולח מייל ולא מפעיל מחדש)</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={this.state.autoRestartEnabled}
                onChange={(e) => this.setState({ autoRestartEnabled: e.target.checked })}
              />
            }
            label={<Typography variant="body2">הפעלה אוטומטית</Typography>}
          />
          <Typography variant="body2">
            מדליקים הפעלה אוטומטית רק אחרי שבדיקת המוכנות עברה על שני השרתים, ואחרי תרגול לילי אחד.
          </Typography>
          <TextField
            label="RAM אזהרה %"
            type="number"
            value={this.state.memoryWarnPercent}
            onChange={(e) => this.setState({ memoryWarnPercent: e.target.value })}
            margin="dense"
          />
          <TextField
            label="Swap אזהרה %"
            type="number"
            value={this.state.swapWarnPercent}
            onChange={(e) => this.setState({ swapWarnPercent: e.target.value })}
            margin="dense"
          />
          <TextField
            label="EBS reads / 5 דק'"
            type="number"
            value={this.state.ioReadOpsWarn}
            onChange={(e) => this.setState({ ioReadOpsWarn: e.target.value })}
            margin="dense"
          />
          <TextField
            label="CPU אזהרה %"
            type="number"
            value={this.state.cpuWarnPercent}
            onChange={(e) => this.setState({ cpuWarnPercent: e.target.value })}
            margin="dense"
          />
          <div className={classes.actions}>
            <Button variant="contained" onClick={this.save}>שמירה</Button>
          </div>
          {status?.rollout && (
            <div>
              {(status.rollout.steps || []).map((step) => (
                <Typography key={step} variant="body2">{step}</Typography>
              ))}
            </div>
          )}
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6">מדדי המסד</Typography>
          <Typography variant="body2" color="textSecondary">
            24 שעות אחרונות מהשרת הראשי. רענון מהשרת המשני נשאר על אותם מדדים.
          </Typography>
          {metrics.length ? (
            <div className={classes.chart}>
              <ResponsiveContainer>
                <LineChart data={metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={24} />
                  <YAxis domain={[0, 100]} unit="%" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="cpu" name="CPU" stroke="#1565c0" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="mem" name="RAM" stroke="#c62828" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="swap" name="Swap" stroke="#ef6c00" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Typography variant="body2">עדיין אין דגימות CloudWatch</Typography>
          )}
          {metrics.length ? (
            <div className={classes.chart}>
              <ResponsiveContainer>
                <LineChart data={metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={24} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="readOps" name="EBS reads / 5 דק'" stroke="#6a1b9a" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : null}
          {pings.length ? (
            <div className={classes.chart}>
              <ResponsiveContainer>
                <LineChart data={pings}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={24} />
                  <YAxis unit=" ms" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="pingMs" name="פינג" stroke="#00897b" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6">אירועים</Typography>
          {(events || []).slice(0, 30).map((event) => (
            <div key={`${event.at}-${event.type}`} className={classes.row}>
              <Typography variant="body2">{event.at}</Typography>
              <Typography variant="body2">
                {event.type} {event.from ? `${event.from} → ${event.to}` : ""}
              </Typography>
            </div>
          ))}
          <Typography variant="body2">
            יומני הזמנות ממתינים: {(status?.journals || []).length}
          </Typography>
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles)(DbHealthMonitor);
